import {ApplicationPolicy, ClientCountryBasedConfig, FeeMode} from "../configuration.model";
import {AccreditationBody, FeeType, StandardName} from "../../store/state/order.state";

export type ClientCountryConfigs = { [country: string]: ClientCountryBasedConfig };

export const CLIENT_COUNTRY_CONFIGS: ClientCountryConfigs = {
	"US": {
		accreditationBody: AccreditationBody.ANAB,
		multisiteContactFormAvailable: true,
		assigneeFilterForCountry: ['US', 'PL', 'ES', 'MX', 'IN'],
		order: {
			pricing: {
				format: '1.0-0', // as defined in the DecimalPipe
				integersWithoutDecimalPart: false,
				showDailyRate: true,
				currency: {
					symbol: '$',
					format: '$ {number}',
				},
				show3YearsTotalForClient: true
			},

			showBillingInfo: true,
			showScheduleCallBanner: true,
			showIso9001CatastrophicRisk: true,
			showPartTimeEmployees: true,
			iso27xScopeFixed: true,
			additionalParamsIncluded: {
				kvkNr: false,
				taxId: false,
			},
			additionalParamsPatterns: null
		},

		pricingAdjustments: {
			fees: [
				{
					type: FeeType.CERTIFICATION_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_YEAR,
					mode: FeeMode.PER_YEAR
				},
				{
					type: FeeType.PRE_AUDIT_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'PRE AUDIT DAILY RATE',
					placeholder: 'Pre audit',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.STAGE_1_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 1 DAILY RATE',
					placeholder: 'Stage 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.STAGE_2_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 2 DAILY RATE',
					placeholder: 'Stage 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.SURVEILLANCE_1_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 1 DAILY RATE',
					placeholder: 'Surveillance 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.SURVEILLANCE_2_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 2 DAILY RATE',
					placeholder: 'Surveillance 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.RECERTIFICATION_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'RECERTIFICATION DAILY RATE',
					placeholder: 'Recertification daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.CERTIFICATION_FEE_YEAR_1,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE YEAR 1',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_CYCLE,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.CERTIFICATION_FEE_YEAR_2,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE YEAR 2',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_CYCLE,
					mode: FeeMode.PER_EVENT
				},{
					type: FeeType.CERTIFICATION_FEE_YEAR_3,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE YEAR 3',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
			]
		}
	},
	"CA": {
		accreditationBody: AccreditationBody.ANAB,
		multisiteContactFormAvailable: true,
		assigneeFilterForCountry: ['US', 'PL', 'ES', 'MX', 'IN'],
		order: {
			pricing: {
				format: '1.0-0', // as defined in the DecimalPipe,
				integersWithoutDecimalPart: false,
				showDailyRate: true,
				currency: {
					symbol: '$',
					format: '$ {number}',
				},
				show3YearsTotalForClient: true
			},

			showBillingInfo: true,
			showScheduleCallBanner: true,
			showIso9001CatastrophicRisk: true,
			showPartTimeEmployees: true,
			iso27xScopeFixed: true,
			additionalParamsIncluded: {
				kvkNr: false,
				taxId: false,
			},
			additionalParamsPatterns: null,
			zipCodeCorrector: (zipCode: string) => {
				if (zipCode == null) {
					return null;
				}

				let corrected = zipCode.toUpperCase();
				if (corrected.length >= 4 && corrected.charAt(3) != ' ' && corrected.charAt(3) != '-') {
					corrected = corrected.substring(0, 3) + ' ' + corrected.substring(3, corrected.length);
				}

				if (corrected.length > 7) {
					corrected = corrected.substring(0, 7)
				}

				return corrected;
			}
		},

		pricingAdjustments: {
			fees: [
				{
					type: FeeType.CERTIFICATION_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_YEAR,
					mode: FeeMode.PER_YEAR
				},
				{
					type: FeeType.PRE_AUDIT_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'PRE AUDIT DAILY RATE',
					placeholder: 'Pre audit',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.STAGE_1_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 1 DAILY RATE',
					placeholder: 'Stage 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.STAGE_2_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 2 DAILY RATE',
					placeholder: 'Stage 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.SURVEILLANCE_1_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 1 DAILY RATE',
					placeholder: 'Surveillance 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.SURVEILLANCE_2_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 2 DAILY RATE',
					placeholder: 'Surveillance 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.RECERTIFICATION_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'RECERTIFICATION DAILY RATE',
					placeholder: 'Recertification daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},]
		}
	},
	"PL": {
		accreditationBody: AccreditationBody.ANAB,
		multisiteContactFormAvailable: true,
		assigneeFilterForCountry: ['US', 'PL', 'ES', 'MX', 'IN'],
		order: {
			pricing: {
				format: '1.0-0', // as defined in the DecimalPipe
				integersWithoutDecimalPart: false,
				showDailyRate: false,
				currency: {
					symbol: 'PLN',
					format: '{number} PLN',
				},
				show3YearsTotalForClient: false
			},

			showBillingInfo: false,
			showScheduleCallBanner: true,
			showIso9001CatastrophicRisk: true,
			showPartTimeEmployees: true,
			iso27xScopeFixed: true,
			additionalParamsIncluded: {
				kvkNr: false,
				taxId: true
			},
			additionalParamsPatterns: {
				taxId: '^\\d{10}$'
			},
			zipCodeCorrector: (zipCode: string) => {
				if (zipCode == null) return null;

				let corrected = zipCode.toUpperCase();
				if (corrected.length >= 3 && corrected.charAt(2) != '-') {
					corrected = corrected.substring(0, 2) + '-' + corrected.substring(2, corrected.length);
				}

				if (corrected.length > 6) {
					corrected = corrected.substring(0, 6)
				}

				return corrected;
			}
		},

		pricingAdjustments: {
			fees: [
				{
					type: FeeType.PRE_AUDIT_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'PRE AUDIT DAILY RATE',
					placeholder: 'Pre audit',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.STAGE_1_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 1 DAILY RATE',
					placeholder: 'Stage 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.STAGE_2_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 2 DAILY RATE',
					placeholder: 'Stage 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.SURVEILLANCE_1_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 1 DAILY RATE',
					placeholder: 'Surveillance 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.SURVEILLANCE_2_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 2 DAILY RATE',
					placeholder: 'Surveillance 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.RECERTIFICATION_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'RECERTIFICATION DAILY RATE',
					placeholder: 'Recertification daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
			]
		}
	},
	"NL": {
		accreditationBody: AccreditationBody.RVA,
		multisiteContactFormAvailable: false,
		assigneeFilterForCountry: ['NL'],
		order: {
			pricing: {
				format: '1.2-2', // as defined in the DecimalPipe
				integersWithoutDecimalPart: true,
				showDailyRate: true,
				currency: {
					symbol: 'EUR',
					format: '{number} EUR',
				},
				show3YearsTotalForClient: true
			},

			showBillingInfo: false,
			showScheduleCallBanner: true,
			showIso9001CatastrophicRisk: false,
			showPartTimeEmployees: false,
			iso27xScopeFixed: false,
			additionalParamsIncluded: {
				kvkNr: true,
				taxId: true
			},
			additionalParamsPatterns: {
				kvkNr: '^\\d{8}$',
				taxId: '^NL\\d{9}B\\d{2}$'
			},
			zipCodeCorrector: (zipCode: string) => {
				if (zipCode == null) return null;

				let corrected = zipCode.toUpperCase();
				if (corrected.length >= 5 && corrected.charAt(4) != ' ') {
					corrected = corrected.substring(0, 4) + ' ' + corrected.substring(4, corrected.length);
				}

				if (corrected.length > 7) {
					corrected = corrected.substring(0, 7)
				}

				return corrected;
			}
		},

		pricingAdjustments: {
			fees: [
				{
					type: FeeType.CERTIFICATION_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_CYCLE,
					mode: FeeMode.PER_YEAR
				},
				{
					type: FeeType.ADMINISTRATION_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'ADMIN. FEE',
					placeholder: 'Administration fee',
					applicationPolicy: ApplicationPolicy.EVERY_YEAR,
					mode: FeeMode.PER_YEAR
				},
				{
					type: FeeType.REGISTRATION_FEE,
					excludeForProducts: [StandardName.ISO_9001, StandardName.ISO_27701, StandardName.ISO_27001],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SCCM FEE',
					placeholder: 'Registration fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_CYCLE,
					mode: FeeMode.PER_YEAR
				},
				{
					type: FeeType.PRE_AUDIT_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'PRE AUDIT DAILY RATE',
					placeholder: 'Pre audit',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.STAGE_1_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 1 DAILY RATE',
					placeholder: 'Stage 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.STAGE_2_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 2 DAILY RATE',
					placeholder: 'Stage 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.SURVEILLANCE_1_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 1 DAILY RATE',
					placeholder: 'Surveillance 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.SURVEILLANCE_2_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 2 DAILY RATE',
					placeholder: 'Surveillance 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.RECERTIFICATION_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'RECERTIFICATION DAILY RATE',
					placeholder: 'Recertification daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.CERTIFICATION_FEE_PER_EVENT,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE',
					placeholder: 'Cert. fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_CYCLE,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.ADMIN_FEE_YEAR_1,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'ADMIN. FEE YEAR 1',
					placeholder: 'Admin. fee year 1',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.ADMIN_FEE_YEAR_2,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'ADMIN. FEE YEAR 2',
					placeholder: 'Admin. fee year 2',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.ADMIN_FEE_YEAR_3,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'ADMIN. FEE YEAR 3',
					placeholder: 'Admin. fee year 3',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.REGISTRATION_FEE_PER_EVENT,
					excludeForProducts: [StandardName.ISO_9001, StandardName.ISO_27701, StandardName.ISO_27001],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SCCM FEE',
					placeholder: 'SCCM fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				}
			]
		}
	},
	"ES": {
		accreditationBody: AccreditationBody.ANAB,
		multisiteContactFormAvailable: true,
		assigneeFilterForCountry: ['US', 'PL', 'ES', 'MX', 'IN'],
		order: {
			pricing: {
				format: '1.0-0', // as defined in the DecimalPipe
				integersWithoutDecimalPart: false,
				showDailyRate: true,
				currency: {
					symbol: 'EUR',
					format: '{number} EUR',
				},
				show3YearsTotalForClient: true
			},

			showBillingInfo: false,
			showScheduleCallBanner: true,
			showIso9001CatastrophicRisk: true,
			showPartTimeEmployees: true,
			iso27xScopeFixed: true,
			additionalParamsIncluded: {
				kvkNr: false,
				taxId: false,
			},
			additionalParamsPatterns: null
		},

		pricingAdjustments: {
			fees: [{
				type: FeeType.PRE_AUDIT_FEE,
				excludeForProducts: [],
				minAmount: 1,
				maxAmount: 10000,
				label: 'PRE AUDIT DAILY RATE',
				placeholder: 'Pre audit',
				applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
				mode: FeeMode.PER_EVENT
			},
				{
					type: FeeType.STAGE_1_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 1 DAILY RATE',
					placeholder: 'Stage 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.STAGE_2_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 2 DAILY RATE',
					placeholder: 'Stage 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.SURVEILLANCE_1_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 1 DAILY RATE',
					placeholder: 'Surveillance 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.SURVEILLANCE_2_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 2 DAILY RATE',
					placeholder: 'Surveillance 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.RECERTIFICATION_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'RECERTIFICATION DAILY RATE',
					placeholder: 'Recertification daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},]
		}
	},
	"MX": {
		accreditationBody: AccreditationBody.ANAB,
		multisiteContactFormAvailable: true,
		assigneeFilterForCountry: ['US', 'PL', 'ES', 'MX', 'IN'],
		order: {
			pricing: {
				format: '1.0-0', // as defined in the DecimalPipe
				integersWithoutDecimalPart: false,
				showDailyRate: true,
				currency: {
					symbol: 'MXN',
					format: 'MXN {number}',
				},
				show3YearsTotalForClient: true
			},

			showBillingInfo: false,
			showScheduleCallBanner: true,
			showIso9001CatastrophicRisk: true,
			showPartTimeEmployees: true,
			iso27xScopeFixed: true,
			additionalParamsIncluded: {
				kvkNr: false,
				taxId: false,
			},
			additionalParamsPatterns: null
		},

		pricingAdjustments: {
			fees: [
				{
					type: FeeType.CERTIFICATION_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_YEAR,
					mode: FeeMode.PER_YEAR
				},
				{
					type: FeeType.PRE_AUDIT_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'PRE AUDIT DAILY RATE',
					placeholder: 'Pre audit',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.STAGE_1_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 1 DAILY RATE',
					placeholder: 'Stage 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.STAGE_2_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 2 DAILY RATE',
					placeholder: 'Stage 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.SURVEILLANCE_1_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 1 DAILY RATE',
					placeholder: 'Surveillance 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.SURVEILLANCE_2_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 2 DAILY RATE',
					placeholder: 'Surveillance 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.RECERTIFICATION_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'RECERTIFICATION DAILY RATE',
					placeholder: 'Recertification daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.CERTIFICATION_FEE_YEAR_1,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE YEAR 1',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_CYCLE,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.CERTIFICATION_FEE_YEAR_2,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE YEAR 2',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_CYCLE,
					mode: FeeMode.PER_EVENT
				},{
					type: FeeType.CERTIFICATION_FEE_YEAR_3,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE YEAR 3',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
			]
		}
	},
	"IN": {
		accreditationBody: AccreditationBody.ANAB,
		multisiteContactFormAvailable: true,
		assigneeFilterForCountry: ['US', 'PL', 'ES', 'MX', 'IN'],
		order: {
			pricing: {
				format: '1.0-0', // as defined in the DecimalPipe
				integersWithoutDecimalPart: false,
				showDailyRate: true,
				currency: {
					symbol: '$',
					format: '$ {number}',
				},
				show3YearsTotalForClient: true
			},

			showBillingInfo: true,
			showScheduleCallBanner: true,
			showIso9001CatastrophicRisk: true,
			showPartTimeEmployees: true,
			iso27xScopeFixed: true,
			additionalParamsIncluded: {
				kvkNr: false,
				taxId: false,
			},
			additionalParamsPatterns: null
		},

		pricingAdjustments: {
			fees: [
				{
					type: FeeType.CERTIFICATION_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_YEAR,
					mode: FeeMode.PER_YEAR
				},
				{
					type: FeeType.PRE_AUDIT_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'PRE AUDIT DAILY RATE',
					placeholder: 'Pre audit',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.STAGE_1_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 1 DAILY RATE',
					placeholder: 'Stage 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.STAGE_2_DAILY_RATE_FEE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'STAGE 2 DAILY RATE',
					placeholder: 'Stage 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT,
					excludeForTransfer: true
				},
				{
					type: FeeType.SURVEILLANCE_1_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 1 DAILY RATE',
					placeholder: 'Surveillance 1 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.SURVEILLANCE_2_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'SURVEILLANCE 2 DAILY RATE',
					placeholder: 'Surveillance 2 daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.RECERTIFICATION_DAILY_RATE,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'RECERTIFICATION DAILY RATE',
					placeholder: 'Recertification daily rate',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.CERTIFICATION_FEE_YEAR_1,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE YEAR 1',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_CYCLE,
					mode: FeeMode.PER_EVENT
				},
				{
					type: FeeType.CERTIFICATION_FEE_YEAR_2,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE YEAR 2',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_CYCLE,
					mode: FeeMode.PER_EVENT
				},{
					type: FeeType.CERTIFICATION_FEE_YEAR_3,
					excludeForProducts: [],
					minAmount: 1,
					maxAmount: 10000,
					label: 'CERT. FEE YEAR 3',
					placeholder: 'Certification fee',
					applicationPolicy: ApplicationPolicy.EVERY_AUDIT_EVENT,
					mode: FeeMode.PER_EVENT
				},
			]
		}
	}
};
export default CLIENT_COUNTRY_CONFIGS
